import { forwardRef, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { ChatStreamingTextProps } from './types';

/**
 * How many words, counting back from the end, are ever their own element. Older text is one plain text
 * node, so the DOM and the number of live filters stay constant however long the reply gets, and a
 * burst of words (a stalled connection catching up) shows the older ones instantly instead of starting
 * dozens of effects at once. Must be larger than the six blurred words plus the ones still easing
 * out of the blur; ten leaves room at the pace a reply streams.
 */
const TAIL_WORDS = 10;

/**
 * A finished reply does not snap sharp: it keeps ageing as if more words were still arriving, one step
 * every `DRAIN_MS`, until the last of them is past the final blur level. That reuses the exact slope and
 * the exact transition the stream itself runs on, so the end is the same motion as the middle, not a
 * second, separate easing that could disagree with it. 70ms is the pace words arrive at.
 */
const DRAIN_MS = 70;

/** Blur levels in `design-system.css` (`data-age` 0 to 5); a word aged past this has no filter. */
const BLUR_LEVELS = 6;

/**
 * Once every word is sharp, how long before the word elements collapse into plain text: the
 * `--motion-overlay` transition (200ms) of the last step, plus a frame or two of margin.
 */
const SETTLE_MS = 260;

const segmenter =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter(undefined, { granularity: 'word' }) : null;

type Group = { start: number; end: number };

/**
 * Splits text into the units that blur: a word together with the punctuation, quotes and emoji stuck to
 * it, so a full stop never stays sharp beside a blurred word.
 *
 * `Intl.Segmenter` finds where a word ends in scripts with no spaces (Chinese, Japanese, Thai) and never
 * splits an emoji sequence. Whitespace separates groups and belongs to none. A mark touching a word
 * joins it (the trailing `.` of "point.", the leading `“` of "“Hello"); a mark on its own, such as an
 * emoji between spaces, is a unit of its own. Without `Intl.Segmenter` every run of non-space
 * characters is one unit, so an unspaced script reads as one long word and blurs as one.
 */
const groupsOf = (text: string): Group[] => {
  if (!segmenter) {
    return Array.from(text.matchAll(/\S+/g), ({ 0: match, index }) => ({ start: index, end: index + match.length }));
  }

  const groups: Group[] = [];
  // The group still open: contiguous with the last segment, and whether it holds a word yet.
  let open: (Group & { hasWord: boolean }) | null = null;

  for (const { index, isWordLike, segment } of segmenter.segment(text)) {
    const end = index + segment.length;

    if (/^\s+$/.test(segment)) {
      open = null;
    } else if (isWordLike) {
      if (open && !open.hasWord) {
        // Marks that opened the group (a quote, a bracket) belong to the word that follows.
        open.end = end;
        open.hasWord = true;
      } else {
        open = { start: index, end, hasWord: true };
        groups.push(open);
      }
    } else if (open) {
      open.end = end;
    } else {
      open = { start: index, end, hasWord: false };
      groups.push(open);
    }
  }

  return groups;
};

/**
 * Text that is still arriving: the newest words resolve out of a soft blur and ease sharp as the next
 * one comes in (a focus pull), and nothing moves or changes size.
 *
 * Give it the WHOLE text so far, not the new chunk. It is a string in, a string out: no per-character
 * spans, no per-word timers, no per-word state. Put it in a flat `ChatMessage` with `streaming` set,
 * which carries `aria-busy`; the text is not a live region, so a screen reader is not fed fragments.
 *
 * Set `streaming={false}` when the reply is complete. The blur drains away at the pace words arrived, and
 * then the text collapses to the plain string, so a finished message holds no filters and no spans at all. A
 * component that mounts already finished renders the plain string at once.
 *
 * Words already on screen never move: the body wraps plainly while streaming (see `ChatMessage`), so
 * only the last line grows. Under reduced motion the words simply appear.
 */
export const ChatStreamingText = forwardRef<HTMLSpanElement, ChatStreamingTextProps>(
  ({ text, streaming, ...rest }, ref) => {
    const [drain, setDrain] = useState(0);
    const [settled, setSettled] = useState(!streaming);

    const drained = drain >= BLUR_LEVELS;

    // The drain only runs for a reply that has just finished, and stops at the last blur level: a message
    // that mounted finished, or has settled, keeps no timer for the rest of its life.
    useEffect(() => {
      if (streaming) {
        setDrain(0);
        setSettled(false);

        return undefined;
      }

      if (settled || drained) return undefined;

      const timer = window.setInterval(() => setDrain((current) => Math.min(current + 1, BLUR_LEVELS)), DRAIN_MS);

      return () => window.clearInterval(timer);
    }, [streaming, settled, drained]);

    useEffect(() => {
      if (streaming || !drained) return undefined;

      const timer = window.setTimeout(() => setSettled(true), SETTLE_MS);

      return () => window.clearTimeout(timer);
    }, [streaming, drained]);

    if (settled && !streaming) {
      return (
        <span ref={ref} {...rest}>
          {text}
        </span>
      );
    }

    const groups = groupsOf(text);
    const tailStart = groups.length > TAIL_WORDS ? groups[groups.length - TAIL_WORDS].start : 0;
    const tail = groups.filter(({ start }) => start >= tailStart);
    const nodes: ReactNode[] = [];
    let cursor = tailStart;

    for (const [position, { start, end }] of tail.entries()) {
      // Whitespace between units is plain text, never an element.
      if (start > cursor) nodes.push(text.slice(cursor, start));

      nodes.push(
        // Keyed by the unit's position in the text, so it keeps its element as more arrives and its
        // fade-in does not replay.
        <span key={start} className="ds-stream-word" data-age={tail.length - 1 - position + drain}>
          {text.slice(start, end)}
        </span>
      );
      cursor = end;
    }

    if (cursor < text.length) nodes.push(text.slice(cursor));

    return (
      <span ref={ref} {...rest} data-finishing={streaming ? undefined : 'true'} data-streaming="true">
        {text.slice(0, tailStart)}
        {nodes}
      </span>
    );
  }
);
